import React from "react";
import { useTranslation } from "react-i18next";
import { IconFilter, IconFilterOff, IconFilterPlus } from "@tabler/icons-react";
import clsx from "clsx";
import { PropertyBrowserTreeNode } from "api-client";
import useWindowOverflow from "hooks/useWindowOverflow";
import { observer } from "mobx-react-lite";
import LinearLoading from "components/LinearLoading";
import FilterRow from "./FilterRow";
import { EMPTY_FILTER_RULE } from "features/DynamicTable/consts";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";
import { globalStore, SIDEBAR_WIDTH } from "store/GlobalStore";

const wrapperID = "filter-wrapper";

function FilterMenu({
  cursorPosition,
  items,
  onChangeProperty,
}: {
  cursorPosition: { x: number; y: number };
  items: PropertyBrowserTreeNode[];
  onChangeProperty: (value: PropertyBrowserTreeNode[], index: number) => void;
}) {
  const { t } = useTranslation();
  const { getLeftPos, containerRef } = useWindowOverflow();
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [scrollPos, setScrollPos] = React.useState<number>(0);

  const onScrollDiv = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const containerElement = e.target as HTMLDivElement;
    setScrollPos(containerElement.scrollTop);
  };

  const addRule = () => {
    dynamicTableStore.filterStore.updateFilterRules([
      ...dynamicTableStore.filterStore.selectedFilterByItems.rules,
      { ...EMPTY_FILTER_RULE },
    ]);
    scrollAreaRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const xPos = globalStore.sidebarOpened
    ? Number(cursorPosition.x) - SIDEBAR_WIDTH
    : Number(cursorPosition.x);

  return (
    <div
      className={clsx(
        "absolute z-5 my-1 w-full overflow-visible rounded-md bg-white text-sm text-gray-700 !max-w-fit",
        "shadow-lg border border-gray-300 min-w-auto w-auto max-w-md focus:outline-none sm:text-sm"
      )}
      ref={containerRef}
      style={{
        left: getLeftPos(xPos, 20),
      }}
      onClick={(e) => e.stopPropagation()}
      id={wrapperID}
    >
      <div className="flex items-center px-4 py-1 rounded-t-md border-b border-gray-300 bg-gray-50 text-xs text-gray-500">
        <IconFilter size={19} className="mr-2" stroke={2.5} />
        {`${t("mappingEditor.filtered")} ${t("mappingEditor.by")}`}
      </div>
      {dynamicTableStore.dataFetchLoading && (
        <LinearLoading type="auto" containerClassName="top-7 h-1" />
      )}
      <div
        className={clsx("pt-1.5 max-h-[325px]", {
          "overflow-y-auto":
            dynamicTableStore.filterStore.selectedFilterByItems.rules.length >
            6,
        })}
        ref={scrollContainerRef}
        onScroll={onScrollDiv}
      >
        {dynamicTableStore.filterStore.selectedFilterByItems.rules.map(
          (rule, index) => (
            <FilterRow
              key={index}
              rule={rule}
              index={index}
              items={items}
              onChangeProperty={onChangeProperty}
              scrollPos={scrollPos}
              wrapperID={wrapperID}
            />
          )
        )}
        <div className="h-1.5" ref={scrollAreaRef}></div>
      </div>
      <div className="border-t border-gray-200 transition text-gray-700 hover:text-gray-900">
        <div
          className="flex px-4 py-2 items-center cursor-pointer text-gray-700 hover:text-indigo-700 hover:bg-indigo-100 group/rule"
          onClick={addRule}
        >
          <IconFilterPlus
            size={20}
            className="mr-2 group-hover/rule:text-indigo-700"
          />
          <span>{t("mappingEditor.addFilterRule")}</span>
        </div>
        <div
          className={clsx(
            "flex px-4 py-2 items-center cursor-pointer",
            dynamicTableStore.filterStore.validFilterRules
              ? " text-gray-700 hover:text-indigo-700 hover:bg-indigo-100 group/remove"
              : "pointer-events-none cursor-not-allowed text-gray-300"
          )}
          onClick={() =>
            dynamicTableStore.filterStore.removeAllFilteringRules()
          }
        >
          <IconFilterOff
            className="mr-2 group-hover/remove:text-indigo-700"
            size={20}
          />
          <span>{t("mappingEditor.removeFilterRules")}</span>
        </div>
      </div>
    </div>
  );
}

export default observer(FilterMenu);
